import React from 'react';
import { HashRouter as Router, Route, Routes, Outlet, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import './style/theme.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import Banner from './layout/Banner';
import SideBar from './layout/SideBar';
import Login from './components/auth/LoginWithOtp';
import Companies from './pages/Companies';
import Locations from './pages/Locations';
import User from './pages/Users.js';
import CollectionCenter from './pages/CollectionCenter';
import AddEntry from './pages/AddEntry.js';
import AssetCategory from './pages/AssetCategory';
import VendorMaster from './pages/VendorMaster';
import GRN from './pages/GRN';
import StockReport from './pages/LiveStockReport';
import Disposals from './pages/Disposals';
import Dashboard from './components/Dashboard/dashboard';
import Transfer from './pages/transfer';
import VehicleRoutes from './pages/vm';
import AgreementRoutes from './pages/agr';
import MachineRoutes from './pages/mm';
import CouponRoutes from './pages/cs';
import UserProfile from './pages/UserProfile';
import BillParameter from './pages/BillParameter';
import BillDetails from './pages/BillDetail';
import FestivalRoutes from './pages/festival';
import { useDispatch, useSelector } from 'react-redux';
import { get } from './services/apiController';
import { setPermissions } from './store/reducers/permission_reducer';
import BillConsolidatedReport from './pages/BillConsolidatedReport';
import InventoryRoutes from './pages/inventory';
import IndentPrint from './pages/inventory/indentPrint';
import GrnPrint from './pages/inventory/grn/GrnPrint';
import JWGrnPrint from './pages/inventory/jw-grn/JWGrnPrint';
import JWDamagePrint from './pages/inventory/jw-damage/JWDamagePrint.js';
import ProductionIndentPrint from './pages/inventory/production-indent/productionIndentPrint';
import DebitNotePrint from './pages/inventory/debit-note/components/DebitNotePrint';
import CakePrint from './pages/cake/order-print';
import CakeRoutes from './pages/cake';
import AdsRoutes from './pages/ads/index.js';
import DepreciationReport from './pages/fa/FADepreciationReport';
import FAD from './pages/fa/FAD';
import PmDcView from './pages/inventory/pm-delivery-challan/deliveryChallanView';
import KitchenConsolidatedPrint from './pages/inventory/kitchen-consolidated-report/KitchenConsolidatedPrint';
import DepreciationConsolidatedReport from './pages/fa/ConsolidatedReport.jsx';
import { setUserRoles, setUserLogin } from './store/reducers/account_reducer';
import { currentRole } from './store/reducers/menu_reducer';
import { exposedRoutes } from './util';
import axios from './services/axios-config.js';

const AppBody = () => {
  return (
    <React.Fragment>
      <Banner />
      <div className="content-wrapper">
        <SideBar />
        <main className="admin-content-wrapper">
          <Outlet />
        </main>
      </div>
    </React.Fragment>
  );
};

const PrivateRoute = (props) => {
  return <AppBody {...props} />;
};

// Main app component wrapped by Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Inner component that has access to router context
function AppContent() {
  const loggedIn = useSelector((state) => state.account.isLoggedIn);
  const dispatch = useDispatch();
  const location = useLocation();

  const navigate = useNavigate();

  const isExposedRoute = (pathname) => {
    return exposedRoutes.some((route) => pathname.startsWith(route));
  };

  const fetchPermissions = () => {
    get('/users/current/permissions')
      .then((response) => {
        dispatch(setPermissions(response));
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const currentUser = async () => {
    const meCallResponse = await axios.get('/v1/users/me');
    if (!meCallResponse) return;
    const userRoles = meCallResponse.data.data.tokens.map((roleToken) => roleToken.role);
    const roleTokens = meCallResponse.data.data.tokens;
    dispatch(setUserRoles(userRoles));
    // checking for priotiry roles
    let currentRoleObject = roleTokens.find((roleToken) => roleToken.role === 'admin' || roleToken.role === 'super-admin');
    if (!currentRoleObject) {
      currentRoleObject = roleTokens[0];
    }
    dispatch(currentRole(currentRoleObject.role));
    dispatch(setUserLogin());
    if (!isExposedRoute(location.pathname)) {
      navigate('/dashboard');
    }
  };

  React.useEffect(() => {
    if (loggedIn && !isExposedRoute(location.pathname)) {
      fetchPermissions();
    }
  }, [loggedIn]);

  React.useEffect(() => {
    if (!isExposedRoute(location.pathname) && location.pathname !== '/') {
      currentUser();
    }
  }, []);

  return (
    <Routes>
      <Route element={<PrivateRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/users" element={<User />} />
        <Route path="/userprofile" element={<UserProfile />} />
        <Route path="/collectioncenter" element={<CollectionCenter />} />
        <Route path="/bill-parameters" element={<BillParameter />} />
        <Route path="/bill-details" element={<BillDetails />} />
        <Route path="/bill-report" element={<BillConsolidatedReport />} />
        <Route path="/entries" element={<AddEntry />} />
        <Route path="/assetcategory" element={<AssetCategory />} />
        <Route path="/vendormaster" element={<VendorMaster />} />
        <Route path="/grn" element={<GRN />} />
        <Route path="/stockreport" element={<StockReport />} />
        <Route path="/depreciation-report" element={<DepreciationReport />} />
        <Route path="/fa-depreciation" element={<FAD />} />
        <Route path="/depreciation-consolidated-report" element={<DepreciationConsolidatedReport />} />
        <Route path="/disposals" element={<Disposals />} />
        <Route path="/assetstransfer" element={<Transfer />} />
        <Route path="/vm/*" element={<VehicleRoutes />} />
        <Route path="/agr/*" element={<AgreementRoutes />} />
        <Route path="/mm/*" element={<MachineRoutes />} />
        <Route path="/cs/*" element={<CouponRoutes />} />
        <Route path="/fest/*" element={<FestivalRoutes />} />
        <Route path="/inv/*" element={<InventoryRoutes />} />
        <Route path="/cake/*" element={<CakeRoutes />} />
        <Route path="/ks-ads/*" element={<AdsRoutes />} />
      </Route>
      <Route path="indents/print/:id" element={<IndentPrint />} />
      <Route path="inv/grn/print/:id" element={<GrnPrint />} />
      <Route path="inv/jw-grn/print/:id" element={<JWGrnPrint />} />
      <Route path="inv/jw-damage/print/:id" element={<JWDamagePrint />} />
      <Route path="inv/production-indent/print/:id" element={<ProductionIndentPrint />} />
      <Route path="inv/debit-note/print/:id" element={<DebitNotePrint />} />
      <Route path="inv/kitchen-consolidated-report/print" element={<KitchenConsolidatedPrint />} />
      <Route path="inv/pm-delivery-challan/:id" element={<PmDcView />} />
      <Route path="cake/order-print/:orderId" element={<CakePrint />} />
      <Route exact path="/" element={<Login />} />
    </Routes>
  );
}

export default App;
